import { Box, Button, Stack, TextField, Typography } from '@mui/material'
import CustomDialog from '../CustomDialog'
import { useDispatch, useSelector } from 'react-redux'
import { Project } from '../../models/Organization'
import DisplayErrorPopup from '../DisplayError'
import { addProjectAsync } from '../../services/projectsService'
import {
  AttributeActionNames,
  AttributeResourceNames,
} from '../../models/AccessControl'
import { Can } from '../../utils/AbilityUtil'
import DropdownWithPlaceholder from '../DropdownWithPlaceholder'
import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '../../store'
import { ChangeEvent, useEffect, useState } from 'react'
import { showErrorMessage } from '../../actions/organization'

const clientsSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.clients,
)
const projectsSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.projects,
)

const AddProject = (props: any) => {
  const dispatch = useDispatch<any>()
  const clients = useSelector(clientsSelector)
  const projects = useSelector(projectsSelector)
  const { isOpen, handleClose, selectedClient, selectedOrganization } = props
  const [clientName, setClientName] = useState<string>('')
  const [projectName, setProjectName] = useState<string>('')
  const [isAddToNewClient, setIsAddToNewClient] = useState<boolean>(false)
  const [selectedClientId, setSelectedClientId] = useState<string>()

  const handleSubmit = (project: Project) => {
    if (!!!projectName) {
      dispatch(showErrorMessage('Project name is required'))
      return
    }

    const isProjectNameExists = projects.find(
      (project) => project.name === projectName,
    )
    if (isProjectNameExists) {
      dispatch(
        showErrorMessage('Project name should be unique within organization'),
      )
      return
    }

    if (!isAddToNewClient && !!!selectedClientId) {
      dispatch(showErrorMessage('Client not selected when creating a new file'))
      return
    }

    if (isAddToNewClient) {
      const isClientNameExists = clients.find(
        (client) => client.name === clientName,
      )
      if (isClientNameExists) {
        dispatch(
          showErrorMessage('Client name should be unique within organization'),
        )
        return
      }
    }

    dispatch(
      addProjectAsync({
        projectName,
        clientName,
        clientId: isAddToNewClient ? undefined : selectedClientId,
        organizationId: Number(selectedOrganization.id),
      }),
    )
  }

  const handleClientSelect = (selectedItemId: string) => {
    setSelectedClientId(selectedItemId)
  }

  const handleProjectNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setProjectName(event.target.value)
  }

  const handleClientNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setClientName(event.target.value)
  }

  useEffect(() => {
    if (!isOpen) {
      setClientName('')
      setProjectName('')
    }
  }, [isOpen])

  return (
    <Can I={AttributeActionNames.Create} a={AttributeResourceNames.Projects}>
      <CustomDialog
        isOpen={isOpen}
        title='Create a new project'
        handleClose={handleClose}
        handleSubmit={handleSubmit}
      >
        <DisplayErrorPopup />
        {isAddToNewClient ? (
          <Stack rowGap={'15px'}>
            <TextField
              sx={{
                width: '389px',
                '& .MuiInputBase-input': {
                  color: '#04002E',
                  fontSize: '14px',
                  lineHeight: 'normal',
                },
                '& .MuiOutlinedInput-root': {
                  boxShadow: '0px 3px 10px 0px rgba(187, 187, 187, 0.25)',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#C3C3C3',
                  opacity: 1,
                  fontSize: '14px',
                },
              }}
              size='small'
              placeholder='Add project name'
              autoFocus
              required
              margin='dense'
              id='name'
              name='name'
              value={projectName}
              onChange={handleProjectNameChange}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: 'normal',
                  color: '#989898',
                }}
              >
                + Add new client
              </Typography>
              <Button
                disableRipple
                variant='text'
                onClick={() => setIsAddToNewClient(false)}
                sx={{
                  fontSize: '10px',
                  lineHeight: 'normal',
                  textTransform: 'none',
                  color: '#3F69FF',
                  padding: 0,
                }}
              >
                Add to existing client
              </Button>
            </Box>
            <TextField
              sx={{
                width: '389px',
                margin: 0,
                '& .MuiInputBase-input': {
                  color: '#04002E',
                  fontSize: '14px',
                  lineHeight: 'normal',
                },
                '& .MuiOutlinedInput-root': {
                  boxShadow: '0px 3px 10px 0px rgba(187, 187, 187, 0.25)',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#C3C3C3',
                  opacity: 1,
                  fontSize: '14px',
                },
              }}
              size='small'
              placeholder='Add client name'
              autoFocus
              required
              margin='dense'
              id='clientName'
              name='clientName'
              onChange={handleClientNameChange}
            />
          </Stack>
        ) : (
          <Stack rowGap={'15px'}>
            <TextField
              sx={{
                width: '389px',
                '& .MuiInputBase-input': {
                  color: '#04002E',
                  fontSize: '14px',
                  lineHeight: 'normal',
                },
                '& .MuiOutlinedInput-root': {
                  boxShadow: '0px 3px 10px 0px rgba(187, 187, 187, 0.25)',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#C3C3C3',
                  opacity: 1,
                  fontSize: '14px',
                },
              }}
              size='small'
              placeholder='Add project name'
              autoFocus
              required
              margin='dense'
              id='name'
              name='name'
              value={projectName}
              onChange={handleProjectNameChange}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: 'normal',
                  color: '#989898',
                }}
              >
                Add to existing client
              </Typography>
              <Button
                disableRipple
                variant='text'
                onClick={() => setIsAddToNewClient(true)}
                sx={{
                  fontSize: '10px',
                  lineHeight: 'normal',
                  textTransform: 'none',
                  color: '#3F69FF',
                  padding: 0,
                }}
              >
                + Add to new client
              </Button>
            </Box>
            <DropdownWithPlaceholder
              placeholder='Select client'
              required={true}
              listItems={clients?.map((client) => {
                return { id: client.id, name: client.name }
              })}
              handleSelect={handleClientSelect}
            />
          </Stack>
        )}
      </CustomDialog>
    </Can>
  )
}

export default AddProject
